/**
 * Inbox service — thin Axios wrapper for the backend /inbox/* endpoints.
 *
 * Mirrors the pattern of other service modules in src/services/: pure
 * functions that take a companyId + payload and return axios promises.
 * Callers (components, contexts) handle loading state and errors.
 *
 * Convention: every endpoint goes through AxiosInstance so the JWT cookie
 * and base URL are picked up uniformly.
 */

import axios from './AxiosInstance';
import { getApiUrl } from '../utils/ApiUtils';
import { aiOperationHeaders } from './AiOperation';

const config = { withCredentials: true };

/**
 * GET /:companyId/inbox?state=...&source=...&limit=...&offset=...
 * Returns { items: [...], returned: N }
 *
 * Default (no state filter) returns items in non-terminal states.
 * Pass state='*' to include linked/dismissed.
 */
export async function listInbox(companyId, { states, source, q, done, sort, dir, limit, offset } = {}) {
  const params = new URLSearchParams();
  if (states && states.length) params.set('state', states.join(','));
  if (source) params.set('source', source);
  if (q) params.set('q', q);
  if (done) params.set('done', done);
  if (sort) params.set('sort', sort);
  if (dir) params.set('dir', dir);
  if (limit != null) params.set('limit', String(limit));
  if (offset != null) params.set('offset', String(offset));
  const qs = params.toString();
  const url = getApiUrl(`${companyId}/inbox${qs ? `?${qs}` : ''}`);
  const { data } = await axios.get(url, config);
  return data;
}

/**
 * GET /:companyId/inbox/metrics
 * Returns { new, processing, awaiting_approval, linked, dismissed, error, total }.
 * Used by the sidebar badge + filter chip counts.
 */
export async function getInboxMetrics(companyId) {
  const url = getApiUrl(`${companyId}/inbox/metrics`);
  const { data } = await axios.get(url, config);
  return data;
}

/**
 * GET /:companyId/inbox/:id
 */
export async function getInboxItem(companyId, id) {
  const url = getApiUrl(`${companyId}/inbox/${id}`);
  const { data } = await axios.get(url, config);
  return data;
}

/**
 * GET /:companyId/inbox/attachments/:attachmentId/thumbnail
 *
 * Fetches a small JPEG preview (image, or PDF first page) of an attachment
 * for the grid view's document cards. Auth is a bearer header (see
 * AxiosInstance), so we can't point an <img src> straight at the endpoint —
 * we fetch the bytes and hand back a blob: object URL the caller renders and
 * is responsible for revoking.
 *
 * Returns:
 *   string  blob: URL of the JPEG
 *   null    the attachment isn't previewable (backend 204) — caller shows
 *           its own typed placeholder instead
 */
export async function fetchInboxAttachmentThumbnail(companyId, attachmentId) {
  const url = getApiUrl(`${companyId}/inbox/attachments/${attachmentId}/thumbnail`);
  const resp = await axios.get(url, { ...config, responseType: 'blob' });
  // 204 (not previewable) comes back as an empty body.
  if (resp.status === 204 || !resp.data || resp.data.size === 0) return null;
  return URL.createObjectURL(resp.data);
}

/**
 * GET /:companyId/inbox/:id/events
 * Returns { events: [...] } — activity timeline.
 */
export async function getInboxItemEvents(companyId, id, { limit } = {}) {
  const params = new URLSearchParams();
  if (limit != null) params.set('limit', String(limit));
  const qs = params.toString();
  const url = getApiUrl(`${companyId}/inbox/${id}/events${qs ? `?${qs}` : ''}`);
  const { data } = await axios.get(url, config);
  return data;
}

/**
 * POST /:companyId/inbox/:id/dismiss   body { reason? }
 */
export async function dismissInboxItem(companyId, id, { reason } = {}) {
  const url = getApiUrl(`${companyId}/inbox/${id}/dismiss`);
  const { data } = await axios.post(url, { reason }, config);
  return data;
}

/**
 * POST /:companyId/inbox/:id/retry
 */
export async function retryInboxItem(companyId, id) {
  const url = getApiUrl(`${companyId}/inbox/${id}/retry`);
  const { data } = await axios.post(url, {}, config);
  return data;
}

/**
 * POST /:companyId/inbox/:id/reprocess
 * User-initiated "Försök igen med AI" — clears any ai_skipped_reason and
 * resets the item so the next poll cycle re-runs the credit gates.
 */
export async function reprocessInboxItem(companyId, id) {
  const url = getApiUrl(`${companyId}/inbox/${id}/reprocess`);
  const { data } = await axios.post(url, {}, config);
  return data;
}

/**
 * POST /:companyId/inbox/:id/done   body { done }
 * Toggle the reversible "mark as done" flag. Pass done=false to un-mark.
 */
export async function setInboxItemDone(companyId, id, done = true) {
  const url = getApiUrl(`${companyId}/inbox/${id}/done`);
  const { data } = await axios.post(url, { done }, config);
  return data;
}

/**
 * POST /:companyId/inbox/:id/link-supplier-invoice
 * Called by CreateSupplierInvoice on save when the user came in via
 * a `?from_inbox_item=<id>` deep link. Marks the item linked + sets
 * supplier_invoice_id without going through the agent.
 */
export async function linkSupplierInvoice(companyId, inboxItemId, supplierInvoiceId) {
  const url = getApiUrl(`${companyId}/inbox/${inboxItemId}/link-supplier-invoice`);
  const { data } = await axios.post(url, { supplier_invoice_id: supplierInvoiceId }, config);
  return data;
}

/**
 * GET /:companyId/inbox/:id/match-candidates
 *
 * Returns ranked supplier_invoice candidates that may already represent
 * this inbox item (re-sent email, payment reminder for an existing
 * invoice, etc.). Free — pure DB query.
 *
 * Response shape: { candidates: [{ score, supplier_invoice }] }
 *   score ∈ 'high' | 'medium' | 'low'
 *   supplier_invoice: { invoice_id, invoice_number, invoice_date, amount, status, due_date }
 */
export async function getMatchCandidates(companyId, inboxItemId) {
  const url = getApiUrl(`${companyId}/inbox/${inboxItemId}/match-candidates`);
  const { data } = await axios.get(url, config);
  return data;
}

/**
 * POST /:companyId/ai/brief
 *
 * Generates a structured AI summary for an inbox item or document.
 * Costs `ai_brief` credits per call. The brief is persisted on the
 * entity, so re-fetching after this returns the cached value; calling
 * again regenerates (and spends another credit).
 *
 * @param {string} companyId
 * @param {string} entityId
 * @param {object} [opts]
 * @param {'inbox_item' | 'document'} [opts.entityType] - defaults to 'inbox_item'
 */
export async function generateBrief(companyId, entityId, { entityType = 'inbox_item' } = {}) {
  const url = getApiUrl(`${companyId}/ai/brief`);
  const { data } = await axios.post(url, {
    entity_type: entityType,
    entity_id: entityId,
  }, { ...config, headers: aiOperationHeaders() });
  return data;
}

// ---------------------------------------------------------------------------
// Blocklist — sender suppression rules
// ---------------------------------------------------------------------------

/**
 * GET /:companyId/inbox/blocklist
 * Returns { entries: [{ id, match_type, value, notes, created_by, created_at }] }.
 */
export async function getBlocklist(companyId) {
  const url = getApiUrl(`${companyId}/inbox/blocklist`);
  const { data } = await axios.get(url, config);
  return data;
}

/**
 * POST /:companyId/inbox/blocklist
 * Body: { match_type: 'email' | 'domain', value, notes? }
 * Adds a suppression rule. Throws (409) if the rule already exists.
 */
export async function addBlocklistEntry(companyId, { matchType, value, notes } = {}) {
  const url = getApiUrl(`${companyId}/inbox/blocklist`);
  const { data } = await axios.post(url, { match_type: matchType, value, notes }, config);
  return data;
}

/**
 * DELETE /:companyId/inbox/blocklist/:entryId
 */
export async function deleteBlocklistEntry(companyId, entryId) {
  const url = getApiUrl(`${companyId}/inbox/blocklist/${entryId}`);
  const { data } = await axios.delete(url, config);
  return data;
}

/**
 * POST /:companyId/email-inbox/sync
 *
 * Backend-side on-demand sync of ALL active email connections for this
 * tenant. Synchronous — returns counts of items created. Used by the
 * Inkorg "Synka inkorgen" button. The scheduled tick runs the same backend
 * poll; `connections_busy` counts mailboxes that were already mid-poll.
 */
export async function syncInbox(companyId) {
  const url = getApiUrl(`${companyId}/email-inbox/sync`);
  const { data } = await axios.post(url, {}, config);
  return data;
}

/**
 * POST /:companyId/email-inbox/connections/:id/sync
 *
 * Sync just one connection. Used by the per-connection "Synka nu"
 * button on EmailInboxSettings.
 */
export async function syncConnection(companyId, connectionId) {
  const url = getApiUrl(`${companyId}/email-inbox/connections/${connectionId}/sync`);
  const { data } = await axios.post(url, {}, config);
  return data;
}
